import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";

import Landing from "@/pages/Landing";
import About from "@/pages/About";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import RefundPolicy from "@/pages/RefundPolicy";
import Faq from "@/pages/Faq";
import Kontak from "@/pages/Kontak";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import GoogleMapsScraper from "@/pages/scraper/GoogleMapsScraper";
import FacebookScraper from "@/pages/scraper/FacebookScraper";
import InstagramScraper from "@/pages/scraper/InstagramScraper";
import Leads from "@/pages/Leads";
import Devices from "@/pages/Devices";
import Templates from "@/pages/Templates";
import CommunityTips from "@/pages/CommunityTips";
import CommunityArticle from "@/pages/CommunityArticle";
import Blast from "@/pages/Blast";
import EmailBlast from "@/pages/EmailBlast";
import Bantuan from "@/pages/Bantuan";
import BlastHistory from "@/pages/BlastHistory";
import Analytics from "@/pages/Analytics";
import Billing from "@/pages/Billing";
import Profile from "@/pages/Profile";
import Admin from "@/pages/Admin";
import UsageDashboard from "@/pages/UsageDashboard";
import PromoPage from "@/pages/PromoPage";
import PromoThankYou from "@/pages/PromoThankYou";
import AdminAds from "@/pages/AdminAds";
import Blog from "@/pages/Blog";
import BlogDetail from "@/pages/BlogDetail";
import PricingPage from "@/pages/Pricing";
import TestimonialPage from "@/pages/TestimonialPage";
import GoogleMapsScraperLanding from "@/pages/GoogleMapsScraperLanding";
import WhatsAppBlastLanding from "@/pages/WhatsAppBlastLanding";
import CookieConsent from "@/components/CookieConsent";
import { ClerkLoading, ClerkLoaded } from "@clerk/clerk-react";
import { Loader2 } from "lucide-react";

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/about" element={<About />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/refund" element={<RefundPolicy />} />
      <Route path="/faq" element={<Faq />} />
      <Route path="/kontak" element={<Kontak />} />
      <Route path="/login/*" element={<Login />} />
      <Route path="/register/*" element={<Register />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogDetail />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/testimoni" element={<TestimonialPage />} />
      <Route path="/tools/google-maps-scraper" element={<GoogleMapsScraperLanding />} />
      <Route path="/tools/whatsapp-blast" element={<WhatsAppBlastLanding />} />
      <Route path="/promo/:slug" element={<PromoPage />} />
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route
          path="scraper"
          element={<Navigate to="/app/scraper/google-maps" replace />}
        />
        <Route path="scraper/google-maps" element={<GoogleMapsScraper />} />
        <Route path="scraper/facebook" element={<FacebookScraper />} />
        <Route path="scraper/instagram" element={<InstagramScraper />} />
        <Route path="leads" element={<Leads />} />
        <Route path="devices" element={<Devices />} />
        <Route path="templates" element={<Templates />} />
        <Route path="blast" element={<Blast />} />
        <Route path="email-blast" element={<EmailBlast />} />
        <Route path="blast-history" element={<BlastHistory />} />
        <Route path="bantuan" element={<Bantuan />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="usage" element={<UsageDashboard />} />
        <Route path="billing" element={<Billing />} />
        <Route path="community" element={<CommunityTips />} />
        <Route path="community/:slug" element={<CommunityArticle />} />
        <Route path="profile" element={<Profile />} />
        <Route path="admin" element={<Admin />} />
        <Route path="admin/ads" element={<AdminAds />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          {/* Public/Standalone routes — rendered BEFORE Clerk loads so they work even
              when the SPA is loaded from a non-resada.id preview domain (Clerk frontend
              API binds to a specific domain). */}
          <Routes>
            <Route path="/promo/thank-you" element={<PromoThankYou />} />
            <Route
              path="/*"
              element={
                <>
                  <ClerkLoading>
                    <div
                      className="min-h-screen flex flex-col items-center justify-center gap-3"
                      data-testid="clerk-loading"
                    >
                      <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
                      <p className="text-sm text-slate-400">Memuat…</p>
                    </div>
                  </ClerkLoading>
                  <ClerkLoaded>
                    <AppRoutes />
                  </ClerkLoaded>
                </>
              }
            />
          </Routes>
          <CookieConsent />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
